import React, { Fragment } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import type { IconName } from 'Components/frameworks/icon/Icon';
import { Icon } from 'Components/frameworks/icon/Icon';
import { SeeMore } from 'Components/frameworks/see-more/SeeMore';
type Props = {
  className?: string;
  displayLoadMore?: boolean;
  isExclusiveCollection: boolean;
  items: Array<{
    color?: Colors;
    iconName: IconName;
    label: string;
    trackingData?: TrackingData;
    url?: string;
  }>;
  seeLessLabel?: string;
  seeMoreLabel?: string;
  dataTestId?: string;
};
export const ProductStrengths = ({
  className,
  displayLoadMore = true,
  isExclusiveCollection,
  items = [],
  seeLessLabel,
  seeMoreLabel,
  dataTestId = '',
}: Props) => {
  const { isTablet, isDesktop } = useResponsive();
  const track = useTracking();

  if (!items.length) {
    return null;
  }

  const content = (
    <div
      className={classnames(
        'flex flex-wrap',
        {
          '-mx-20': isExclusiveCollection,
          '-mx-10': !isExclusiveCollection,
        },
        className,
      )}
      data-testid={dataTestId}
    >
      {items.map(({ color = COLORS.SAFFRON, iconName, label, trackingData, url }, idx) => {
        const TagName = url ? DynamicLink : 'div';
        return (
          <Fragment key={label}>
            {idx > 0 && !isExclusiveCollection && (
              <span className="animation-duration-750 mt-10 hidden animate-fadeIn font-sans text-44 text-grey opacity-50 sm:block">
                /
              </span>
            )}
            {idx > 0 && (isTablet || idx % 2 !== 0) && isExclusiveCollection && (
              <div className="min-h-full">
                <div className="m-0 mt-40 h-50 w-1 border-r-1 border-grey p-0" />
              </div>
            )}
            <TagName
              className={classnames(
                `flex w-1/2 max-w-1/2 flex-col items-center sm:w-auto sm:flex-1 ${isExclusiveCollection ? 'px-20' : 'p-10'} text-${color} text-center text-12 sm:text-14`,
                {
                  '-mr-1': !isTablet && idx % 2 === 0,
                  [`hocus:text-${color}-active transition-color`]: url,
                },
              )}
              onClick={trackingData ? () => track(trackingData) : undefined}
              {...(url
                ? {
                    url,
                  }
                : null)}
            >
              <Icon
                className={
                  isExclusiveCollection ? 'mb-20 mt-40 text-ultramarine' : 'mb-10 text-sienna'
                }
                name={iconName}
                width={isExclusiveCollection ? '3.125rem' : '3.75rem'}
              />
              <span
                className={classnames('block w-full', {
                  'font-sans': isExclusiveCollection,
                  'mb-30': isExclusiveCollection,
                })}
              >
                {label}
              </span>
            </TagName>
          </Fragment>
        );
      })}
    </div>
  );

  if (!isDesktop && isTablet && displayLoadMore) {
    return (
      <SeeMore
        classNameButton="text-center"
        isCollapseEnabled
        seeLessLabel={seeLessLabel}
        seeMoreLabel={seeMoreLabel}
        seeMoreSize={150}
      >
        {content}
      </SeeMore>
    );
  }

  return content;
};
